import { isPlatformBrowser } from '@angular/common'
import { Injectable, PLATFORM_ID, inject } from '@angular/core'
import type {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router'
import { PRIMARY_OUTLET, TitleStrategy } from '@angular/router'

import { SeoService } from './seo.service'
import type { SeoPageMeta, SeoRouteMeta } from './seo.types'

/**
 * Replaces Angular's `DefaultTitleStrategy` so the whole document head - not
 * just `<title>` - is driven by the route.
 *
 * The router calls this after activation of every successful navigation,
 * including the initial one during SSR, which is the only hook that both sees
 * the resolved route data and can't be beaten to it by a page component. A
 * component that set its own tags would run _before_ this and get overwritten;
 * hence the `data: { seo }` convention instead. Routes without an `seo` entry
 * fall back to the site-wide defaults, which is what clears the previous page's
 * tags on client-side navigation.
 */
@Injectable()
export class SeoTitleStrategy extends TitleStrategy {
  private readonly seoService = inject(SeoService)
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID))

  private isHydratingNavigation = true

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const meta = deepestRouteMeta(snapshot.root)
    const isHydratingNavigation = this.isHydratingNavigation
    this.isHydratingNavigation = false

    // The browser re-runs every resolver on the navigation that hydrates the
    // server-rendered page (nothing transfers the Apollo cache, and the article
    // query is `network-only`). If that refetch fails - offline, an expired
    // token, a flaky API - the route hands us nothing, and falling back to the
    // site defaults would throw away metadata the server already got right for
    // this exact URL. Every later navigation is applied unconditionally: by
    // then the head still describes the page being left behind.
    if (!meta && isHydratingNavigation && this.isBrowser) return

    try {
      this.seoService.apply(meta)
    } catch (error) {
      // The router runs this inside the navigation transition, so a throw here
      // aborts the navigation - and on the server it takes the whole render
      // down with it. The head is decoration next to the page itself, so it is
      // never worth that; log it and let the navigation finish.
      console.error('Error applying SEO metadata:', error)
    }
  }
}

/**
 * The `seo` entry of the deepest primary-outlet route that defines one,
 * mirroring how `TitleStrategy.buildTitle` picks up `Route.title`.
 */
function deepestRouteMeta(root: ActivatedRouteSnapshot): SeoPageMeta | null {
  let route: ActivatedRouteSnapshot | undefined = root
  let meta: SeoPageMeta | null = null

  while (route) {
    const routeMeta = route.data['seo'] as SeoRouteMeta | undefined
    if (typeof routeMeta === 'function') {
      meta = routeMeta(route)
    }
    route = route.children.find(child => child.outlet === PRIMARY_OUTLET)
  }

  return meta
}
